/**
 * Mailtexten för IA:ns tidsförslag — hemläxa, intromöte och avstämning.
 *
 * Texten låg hårdkodad i backend (sendProposalEmail), så IA kunde inte se vad
 * kunden skulle få och inte ändra ett ord. Samma problem som underlagsmailet
 * hade innan SkickaUnderlagModal fick sina mallar, och lösningen är densamma:
 * mallen bor här, modalen visar den redigerbar, och backend skickar det som
 * faktiskt står i rutan.
 *
 * VAD SOM ÄR REDIGERBART: bara stycken mellan hälsningen och portalknappen.
 * Backend äger fortsatt "Hej <förnamn>," (den är per mottagare — ett mail går
 * till varje beställare), knappen till kundportalen, underskriften med
 * projekt-id och sidfoten. IA ska inte kunna råka radera länken kunden behöver.
 *
 * Formatet är ren text. Tom rad = nytt stycke, enkelt radbryt = <br>. Backend
 * gör samma tolkning, så det man ser i rutan är det kunden får.
 */

/** Längden som text, t.ex. "45 min". */
const langdText = (minuter) => `${minuter || 30} min`;

/**
 * Meningen om bilagorna. IA kan bifoga flera underlag — ett per område, t.ex.
 * färgsättning och bad — så ordalydelsen måste följa antalet. Samma tre
 * varianter finns i backends standardmall (meetingController), så texten blir
 * densamma oavsett om IA redigerat rutan eller inte.
 */
const bilagemening = (antalFiler = 0) => {
  if (antalFiler === 1) return ' I bifogat dokument finner du mer information och exempel på hur du kan gå tillväga.';
  if (antalFiler > 1) return ` I de ${antalFiler} bifogade dokumenten finner du mer information och exempel på hur du kan gå tillväga.`;
  return '';
};

const hemlaxa = ({ iaFornamn, minuter, antalFiler }) => [
  'Inför vårt möte vill jag ge dig en hemläxa!',
  'För att på bästa sätt samla dina tankar och idéer görs detta enklast genom att samla dom i en '
    + 'Powerpoint eller liknande program där man sparar ned bilder och gör en kort beskrivning av '
    + 'vad man gillar i bilderna.'
    + bilagemening(antalFiler),
  'När du är klar med hemläxan laddar du upp den på kundportalen (under mötessektionen) så att vi '
    + 'tillsammans kan gå igenom hemläxan under vårt möte.',
  `Jag har föreslagit ett par tider för en kort avstämning (${langdText(minuter)}) via Teams. `
    + 'Svara med vilken tid som funkar så bokar jag in detta.'
].join('\n\n');

const intromote = ({ iaFornamn, minuter }) => [
  `${iaFornamn} heter jag och jobbar som inredningsarkitekt på Leadme. Jag har förstått att ni är `
    + 'intresserade av att höra lite mer om vad vi på inredningssidan kommer och kan hjälpa er med '
    + 'i ert projekt.',
  'Vi brukar alltid boka in en kort sittning via teams där jag berättar mer om hur vi jobbar.',
  `Jag har föreslagit ett par tider för ett kort intromöte (${langdText(minuter)}) som du hittar på kundportalen.`,
  'Svara med vilken tid som funkar så bokar jag in detta.'
].join('\n\n');

const avstamning = ({ minuter, agenda = [] }) => {
  const stycken = [
    `Jag vill boka in en kort avstämning (${langdText(minuter)}) via Teams för att gå igenom status på ert projekt.`
  ];

  const punkter = agenda.map(a => a?.title).filter(Boolean);
  if (punkter.length > 0) {
    stycken.push(['Agenda:', ...punkter.map(p => `- ${p}`)].join('\n'));
  }

  stycken.push(
    'Jag har föreslagit ett par tider som du hittar på kundportalen. Svara med vilken tid som '
    + 'funkar så bokar jag in detta.'
  );
  return stycken.join('\n\n');
};

const MALLAR = { hemlaxa, intromote, avstamning };

/** Mötestyper som har en redigerbar mall. Övriga använder backends standardmail. */
export const harRedigerbarMall = (type) => Object.prototype.hasOwnProperty.call(MALLAR, type);

/**
 * @param {Object} opts
 * @param {string} opts.type - Mötestyp
 * @param {string} opts.iaFornamn - Visas i intromötets första mening
 * @param {number} opts.minuter - Mötets längd, från modalens val
 * @param {number} opts.antalFiler - Antal hemläxefiler som bifogas (0 = ingen)
 * @param {Array} opts.agenda - Agendapunkter ({ title })
 * @returns {string} Ren text, tom rad mellan stycken
 */
export const byggProposalText = ({ type, iaFornamn = 'Jag', minuter, antalFiler = 0, agenda = [] }) => {
  const mall = MALLAR[type];
  if (!mall) return '';
  return mall({ iaFornamn, minuter, antalFiler, agenda });
};
